// src/api/prestamos.js - API para manejo de préstamos personales
import { apiFetch } from './index.js';

// ==================== CONSTANTES ====================

// Estados de préstamo
export const ESTADOS_PRESTAMO = {
  ACTIVO: 'activo',
  PAGADO: 'pagado',
  VENCIDO: 'vencido',
  PARCIAL: 'parcial',
  CANCELADO: 'cancelado'
};

// Tipos de prestatario
export const TIPOS_PRESTATARIO = {
  EMPLEADO: 'empleado',
  CLIENTE: 'cliente',
  EXTERNO: 'externo'
};

// Métodos de pago
export const METODOS_PAGO_PRESTAMO = [
  'efectivo',
  'transferencia',
  'pago_movil',
  'zelle',
  'cheque',
  'deposito'
];

// ==================== FUNCIONES AUXILIARES ====================

/**
 * Convertir de API a frontend (camelCase)
 */
const convertirDesdeAPI = (apiPrestamo) => {
  if (!apiPrestamo) return null;

  let source = apiPrestamo;
  if (apiPrestamo.data && !Array.isArray(apiPrestamo.data) && typeof apiPrestamo.data === 'object') {
    source = apiPrestamo.data;
  } else if (apiPrestamo.prestamo && typeof apiPrestamo.prestamo === 'object') {
    source = apiPrestamo.prestamo;
  }

  const getProp = (obj, propName) => {
    if (!obj) return undefined;
    const keys = Object.keys(obj);
    const normalizedProp = propName.toLowerCase().replace(/_/g, '');
    const foundKey = keys.find(k => k.toLowerCase().replace(/_/g, '') === normalizedProp);
    return foundKey ? obj[foundKey] : undefined;
  };

  const id = source.idPrestamo || source.id || source.id_prestamo ||
             source.IdPrestamo || source.Id || getProp(source, 'idPrestamo') ||
             apiPrestamo.idPrestamo || getProp(apiPrestamo, 'idPrestamo');

  return {
    idPrestamo: id,
    numeroPrestamo: source.numeroPrestamo || source.numero_prestamo || getProp(source, 'numeroPrestamo'),
    idUsuario: source.idUsuario || source.id_usuario || getProp(source, 'idUsuario'),
    nombreUsuario: source.nombreUsuario || source.nombre_usuario || getProp(source, 'nombreUsuario'),
    idCliente: source.idCliente || source.id_cliente || getProp(source, 'idCliente'),
    nombreCliente: source.nombreCliente || source.nombre_cliente || getProp(source, 'nombreCliente'),
    tipoPrestatario: source.tipoPrestatario || source.tipo_prestatario || getProp(source, 'tipoPrestatario'),
    nombrePrestatario: source.nombrePrestatario || source.nombre_prestatario || getProp(source, 'nombrePrestatario'),
    cedulaPrestatario: source.cedulaPrestatario || source.cedula_prestatario || getProp(source, 'cedulaPrestatario'),
    telefonoPrestatario: source.telefonoPrestatario || source.telefono_prestatario || getProp(source, 'telefonoPrestatario'),
    montoUsd: parseFloat(source.montoUsd || source.monto_usd || 0),
    montoBs: parseFloat(source.montoBs || source.monto_bs || 0),
    interesPorcentaje: parseFloat(source.interesPorcentaje || source.interes_porcentaje || 0),
    interesTotalUsd: parseFloat(source.interesTotalUsd || source.interes_total_usd || 0),
    interesTotalBs: parseFloat(source.interesTotalBs || source.interes_total_bs || 0),
    totalDeudaUsd: parseFloat(source.totalDeudaUsd || source.total_deuda_usd || 0),
    totalDeudaBs: parseFloat(source.totalDeudaBs || source.total_deuda_bs || 0),
    saldoPendienteUsd: parseFloat(source.saldoPendienteUsd || source.saldo_pendiente_usd || 0),
    saldoPendienteBs: parseFloat(source.saldoPendienteBs || source.saldo_pendiente_bs || 0),
    fechaPrestamo: source.fechaPrestamo || source.fecha_prestamo || getProp(source, 'fechaPrestamo'),
    fechaVencimiento: source.fechaVencimiento || source.fecha_vencimiento || getProp(source, 'fechaVencimiento'),
    numeroCuotas: parseInt(source.numeroCuotas || source.numero_cuotas || 1),
    metodoPago: source.metodoPago || source.metodo_pago || getProp(source, 'metodoPago'),
    referenciaPago: source.referenciaPago || source.referencia_pago || getProp(source, 'referenciaPago'),
    estado: source.estado || getProp(source, 'estado'),
    motivo: source.motivo || getProp(source, 'motivo'),
    observaciones: source.observaciones || getProp(source, 'observaciones'),
    nombreUsuarioRegistro: source.nombreUsuarioRegistro || source.nombre_usuario_registro || getProp(source, 'nombreUsuarioRegistro'),
    fechaCreacion: source.fechaCreacion || source.fecha_creacion || getProp(source, 'fechaCreacion'),
    fechaActualizacion: source.fechaActualizacion || source.fecha_actualizacion || getProp(source, 'fechaActualizacion'),
    cuotasPagadas: parseInt(source.cuotasPagadas || source.cuotas_pagadas || 0),
    cuotasPendientes: parseInt(source.cuotasPendientes || source.cuotas_pendientes || 0),
    
    ...apiPrestamo,
    ...source,
    idPrestamo: id
  };
};

/**
 * Convertir de frontend a API (camelCase - igual que el frontend)
 * CORREGIDO: La API espera camelCase, no snake_case
 */
const convertirParaAPI = (frontendPrestamo) => {
  return {
    idUsuario: frontendPrestamo.idUsuario || null,
    idCliente: frontendPrestamo.idCliente || null,
    tipoPrestatario: frontendPrestamo.tipoPrestatario || 'empleado',
    nombrePrestatario: frontendPrestamo.nombrePrestatario || '',
    cedulaPrestatario: frontendPrestamo.cedulaPrestatario || '',
    telefonoPrestatario: frontendPrestamo.telefonoPrestatario || '',
    montoUsd: parseFloat(frontendPrestamo.montoUsd || 0),
    montoBs: parseFloat(frontendPrestamo.montoBs || 0),
    interesPorcentaje: parseFloat(frontendPrestamo.interesPorcentaje || 0),
    fechaVencimiento: frontendPrestamo.fechaVencimiento,
    numeroCuotas: parseInt(frontendPrestamo.numeroCuotas || 1),
    metodoPago: frontendPrestamo.metodoPago || 'efectivo',
    referenciaPago: frontendPrestamo.referenciaPago || '',
    motivo: frontendPrestamo.motivo || '',
    observaciones: frontendPrestamo.observaciones || '',
    idUsuarioRegistro: frontendPrestamo.idUsuarioRegistro || null
  };
};

/**
 * Convertir lista desde API
 */
const convertirListaDesdeAPI = (apiLista) => {
  let lista = apiLista;
  if (apiLista && apiLista.data && Array.isArray(apiLista.data)) {
    lista = apiLista.data;
  } else if (apiLista && apiLista.prestamos && Array.isArray(apiLista.prestamos)) {
    lista = apiLista.prestamos;
  }
  if (!Array.isArray(lista)) return [];
  return lista.map(convertirDesdeAPI);
};

/**
 * Convertir cuota desde API
 */
const convertirCuotaDesdeAPI = (apiCuota) => {
  if (!apiCuota) return null;
  return {
    idCuota: apiCuota.idCuota || apiCuota.id,
    idPrestamo: apiCuota.idPrestamo || apiCuota.id_prestamo,
    numeroCuota: apiCuota.numeroCuota || apiCuota.numero_cuota || 0,
    montoUsd: parseFloat(apiCuota.montoUsd || apiCuota.monto_usd || 0),
    montoBs: parseFloat(apiCuota.montoBs || apiCuota.monto_bs || 0),
    saldoUsd: parseFloat(apiCuota.saldoUsd || apiCuota.saldo_usd || 0),
    saldoBs: parseFloat(apiCuota.saldoBs || apiCuota.saldo_bs || 0),
    fechaVencimiento: apiCuota.fechaVencimiento || apiCuota.fecha_vencimiento,
    fechaPago: apiCuota.fechaPago || apiCuota.fecha_pago,
    estado: apiCuota.estado || 'pendiente',
    interesMoraUsd: parseFloat(apiCuota.interesMoraUsd || apiCuota.interes_mora_usd || 0),
    interesMoraBs: parseFloat(apiCuota.interesMoraBs || apiCuota.interes_mora_bs || 0),
    observaciones: apiCuota.observaciones || ''
  };
};

/**
 * Convertir abono desde API
 */
const convertirAbonoDesdeAPI = (apiAbono) => {
  if (!apiAbono) return null;
  return {
    idAbono: apiAbono.idAbono || apiAbono.id,
    idPrestamo: apiAbono.idPrestamo || apiAbono.id_prestamo,
    idCuota: apiAbono.idCuota || apiAbono.id_cuota,
    numeroCuota: apiAbono.numeroCuota || apiAbono.numero_cuota,
    montoUsd: parseFloat(apiAbono.montoUsd || apiAbono.monto_usd || 0),
    montoBs: parseFloat(apiAbono.montoBs || apiAbono.monto_bs || 0),
    tasaBsUsd: parseFloat(apiAbono.tasaBsUsd || apiAbono.tasa_bs_usd || 0),
    fechaAbono: apiAbono.fechaAbono || apiAbono.fecha_abono,
    metodoPago: apiAbono.metodoPago || apiAbono.metodo_pago || 'efectivo',
    referencia: apiAbono.referencia || '',
    tipoAbono: apiAbono.tipoAbono || apiAbono.tipo_abono || 'cuota',
    observaciones: apiAbono.observaciones || '',
    nombreUsuarioRegistro: apiAbono.nombreUsuarioRegistro || apiAbono.nombre_usuario_registro || 'Sistema',
    fechaCreacion: apiAbono.fechaCreacion || apiAbono.fecha_creacion
  };
};

// ==================== FUNCIONES DE FORMATEO ====================

/**
 * Formatear estado de préstamo
 */
export const formatearEstadoPrestamo = (estado) => {
  const estados = {
    'activo': { text: 'Activo', color: 'bg-blue-100 text-blue-700 border-blue-200' },
    'pagado': { text: 'Pagado', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    'vencido': { text: 'Vencido', color: 'bg-rose-100 text-rose-700 border-rose-200' },
    'parcial': { text: 'Parcial', color: 'bg-amber-100 text-amber-700 border-amber-200' },
    'cancelado': { text: 'Cancelado', color: 'bg-gray-100 text-gray-600 border-gray-200' }
  };
  return estados[estado?.toLowerCase()] || { text: estado || 'Desconocido', color: 'bg-gray-100 text-gray-600' };
};

/**
 * Formatear tipo de prestatario
 */
export const formatearTipoPrestatario = (tipo) => {
  const tipos = {
    'empleado': { text: 'Empleado', icon: 'mdi:account-tie' },
    'cliente': { text: 'Cliente', icon: 'mdi:account' },
    'externo': { text: 'Externo', icon: 'mdi:account-outline' }
  };
  return tipos[tipo?.toLowerCase()] || { text: tipo || 'Desconocido', icon: 'mdi:account' };
};

/**
 * Formatear método de pago
 */
export const formatearMetodoPago = (metodo) => {
  const metodos = {
    'efectivo': { text: 'Efectivo', icon: 'mdi:cash' },
    'transferencia': { text: 'Transferencia', icon: 'mdi:bank-transfer' },
    'pago_movil': { text: 'Pago Móvil', icon: 'mdi:cellphone' },
    'zelle': { text: 'Zelle', icon: 'mdi:bank' },
    'cheque': { text: 'Cheque', icon: 'mdi:file-document' },
    'deposito': { text: 'Depósito', icon: 'mdi:bank' }
  };
  return metodos[metodo?.toLowerCase()] || { text: metodo || 'Desconocido', icon: 'mdi:cash' };
};

/**
 * Calcular días para vencimiento
 */
export const calcularDiasParaVencer = (fechaVencimiento) => {
  if (!fechaVencimiento) return null;
  const hoy = new Date();
  const vencimiento = new Date(fechaVencimiento);
  const diffTime = vencimiento - hoy;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

/**
 * Obtener estado de vencimiento
 */
export const obtenerEstadoVencimiento = (fechaVencimiento) => {
  const dias = calcularDiasParaVencer(fechaVencimiento);
  if (dias === null) return 'desconocido';
  if (dias < 0) return 'vencido';
  if (dias <= 3) return 'urgente';
  if (dias <= 7) return 'proximo';
  return 'normal';
};

// ==================== FUNCIONES API ====================

/**
 * Obtener todos los préstamos
 */
export const obtenerPrestamos = async () => {
  try {
    console.log('📋 Obteniendo préstamos...');
    const response = await apiFetch('/Prestamo');
    const prestamos = convertirListaDesdeAPI(response);
    return {
      success: true,
      data: prestamos,
      count: prestamos.length,
      message: 'Préstamos obtenidos exitosamente'
    };
  } catch (error) {
    console.error('❌ Error obteniendo préstamos:', error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

/**
 * Obtener préstamos activos
 */
export const obtenerPrestamosActivos = async () => {
  try {
    console.log('📋 Obteniendo préstamos activos...');
    const response = await apiFetch('/Prestamo/activos');
    const prestamos = convertirListaDesdeAPI(response);
    return {
      success: true,
      data: prestamos,
      count: prestamos.length,
      message: 'Préstamos activos obtenidos'
    };
  } catch (error) {
    console.error('❌ Error obteniendo préstamos activos:', error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

/**
 * Obtener préstamos vencidos
 */
export const obtenerPrestamosVencidos = async () => {
  try {
    console.log('📋 Obteniendo préstamos vencidos...');
    const response = await apiFetch('/Prestamo/vencidos');
    const prestamos = convertirListaDesdeAPI(response);
    return {
      success: true,
      data: prestamos,
      count: prestamos.length,
      message: 'Préstamos vencidos obtenidos'
    };
  } catch (error) {
    console.error('❌ Error obteniendo préstamos vencidos:', error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

/**
 * Obtener préstamos próximos a vencer
 */
export const obtenerPrestamosProximosVencer = async (dias = 7) => {
  try {
    console.log(`📋 Obteniendo préstamos próximos a vencer (${dias} días)...`);
    const response = await apiFetch(`/Prestamo/proximos-vencer?dias=${dias}`);
    const prestamos = convertirListaDesdeAPI(response);
    return {
      success: true,
      data: prestamos,
      count: prestamos.length,
      message: 'Préstamos próximos a vencer obtenidos'
    };
  } catch (error) {
    console.error('❌ Error obteniendo préstamos próximos a vencer:', error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

/**
 * Obtener préstamos por estado
 */
export const obtenerPrestamosPorEstado = async (estado) => {
  try {
    if (!estado) throw new Error('Estado es requerido');
    console.log(`📋 Obteniendo préstamos con estado: ${estado}`);
    const response = await apiFetch(`/Prestamo/estado/${estado}`);
    const prestamos = convertirListaDesdeAPI(response);
    return {
      success: true,
      data: prestamos,
      count: prestamos.length,
      message: `Préstamos con estado ${estado} obtenidos`
    };
  } catch (error) {
    console.error(`❌ Error obteniendo préstamos por estado ${estado}:`, error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

/**
 * Obtener préstamos por tipo de prestatario
 */
export const obtenerPrestamosPorTipo = async (tipo) => {
  try {
    if (!tipo) throw new Error('Tipo es requerido');
    console.log(`📋 Obteniendo préstamos de tipo: ${tipo}`);
    const response = await apiFetch(`/Prestamo/tipo/${tipo}`);
    const prestamos = convertirListaDesdeAPI(response);
    return {
      success: true,
      data: prestamos,
      count: prestamos.length,
      message: `Préstamos de tipo ${tipo} obtenidos`
    };
  } catch (error) {
    console.error(`❌ Error obteniendo préstamos por tipo ${tipo}:`, error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

/**
 * Obtener préstamos por usuario
 */
export const obtenerPrestamosPorUsuario = async (idUsuario) => {
  try {
    if (!idUsuario) throw new Error('ID de usuario es requerido');
    console.log(`📋 Obteniendo préstamos del usuario: ${idUsuario}`);
    const response = await apiFetch(`/Prestamo/usuario/${idUsuario}`);
    const prestamos = convertirListaDesdeAPI(response);
    return {
      success: true,
      data: prestamos,
      count: prestamos.length,
      message: 'Préstamos del usuario obtenidos'
    };
  } catch (error) {
    console.error(`❌ Error obteniendo préstamos del usuario ${idUsuario}:`, error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

/**
 * Obtener préstamos por cliente
 */
export const obtenerPrestamosPorCliente = async (idCliente) => {
  try {
    if (!idCliente) throw new Error('ID de cliente es requerido');
    console.log(`📋 Obteniendo préstamos del cliente: ${idCliente}`);
    const response = await apiFetch(`/Prestamo/cliente/${idCliente}`);
    const prestamos = convertirListaDesdeAPI(response);
    return {
      success: true,
      data: prestamos,
      count: prestamos.length,
      message: 'Préstamos del cliente obtenidos'
    };
  } catch (error) {
    console.error(`❌ Error obteniendo préstamos del cliente ${idCliente}:`, error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

/**
 * Obtener un préstamo por ID
 */
export const obtenerPrestamoPorId = async (id) => {
  try {
    if (!id) throw new Error('ID de préstamo es requerido');
    console.log(`🔍 Obteniendo préstamo ID: ${id}`);
    const response = await apiFetch(`/Prestamo/${id}`);
    return {
      success: true,
      data: convertirDesdeAPI(response),
      message: 'Préstamo obtenido exitosamente'
    };
  } catch (error) {
    console.error(`❌ Error obteniendo préstamo ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Obtener préstamo con detalles completos
 */
export const obtenerPrestamoDetalle = async (id) => {
  try {
    if (!id) throw new Error('ID de préstamo es requerido');
    console.log(`🔍 Obteniendo detalle del préstamo ID: ${id}`);
    const response = await apiFetch(`/Prestamo/${id}/detalle`);
    
    const prestamo = convertirDesdeAPI(response);
    
    if (response.cuotas) {
      prestamo.cuotas = response.cuotas.map(convertirCuotaDesdeAPI);
    }
    
    if (response.abonos) {
      prestamo.abonos = response.abonos.map(convertirAbonoDesdeAPI);
    }
    
    return {
      success: true,
      data: prestamo,
      message: 'Detalle del préstamo obtenido'
    };
  } catch (error) {
    console.error(`❌ Error obteniendo detalle del préstamo ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Obtener préstamo por número
 */
export const obtenerPrestamoPorNumero = async (numero) => {
  try {
    if (!numero) throw new Error('Número de préstamo es requerido');
    console.log(`🔍 Obteniendo préstamo número: ${numero}`);
    const response = await apiFetch(`/Prestamo/numero/${numero}`);
    return {
      success: true,
      data: convertirDesdeAPI(response),
      message: 'Préstamo obtenido'
    };
  } catch (error) {
    console.error(`❌ Error obteniendo préstamo ${numero}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Buscar préstamos por criterios
 */
export const buscarPrestamos = async (criterios) => {
  try {
    console.log('🔍 Buscando préstamos con criterios:', criterios);
    const response = await apiFetch('/Prestamo/search', {
      method: 'POST',
      body: JSON.stringify(criterios)
    });
    const prestamos = convertirListaDesdeAPI(response);
    return {
      success: true,
      data: prestamos,
      count: prestamos.length,
      message: 'Búsqueda completada'
    };
  } catch (error) {
    console.error('❌ Error buscando préstamos:', error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

/**
 * Crear un nuevo préstamo
 */
export const crearPrestamo = async (prestamoData) => {
  try {
    // Validaciones
    if (prestamoData.tipoPrestatario === 'empleado' && !prestamoData.idUsuario) {
      throw new Error('Para empleados debe especificar el ID de usuario');
    }
    if (prestamoData.tipoPrestatario === 'cliente' && !prestamoData.idCliente) {
      throw new Error('Para clientes debe especificar el ID de cliente');
    }
    if (prestamoData.tipoPrestatario === 'externo' && !prestamoData.nombrePrestatario) {
      throw new Error('Para externos debe especificar el nombre');
    }
    if (prestamoData.montoUsd <= 0 && prestamoData.montoBs <= 0) {
      throw new Error('Debe especificar al menos un monto en USD o Bs');
    }
    if (!prestamoData.fechaVencimiento) {
      throw new Error('La fecha de vencimiento es requerida');
    }
    if (!prestamoData.idUsuarioRegistro) {
      throw new Error('El ID de usuario registro es requerido');
    }

    console.log('➕ Creando nuevo préstamo:', prestamoData);
    const apiData = convertirParaAPI(prestamoData);
    console.log('📦 Datos enviados a la API:', apiData);
    
    const response = await apiFetch('/Prestamo', {
      method: 'POST',
      body: JSON.stringify(apiData)
    });

    return {
      success: true,
      data: convertirDesdeAPI(response),
      message: 'Préstamo creado exitosamente'
    };
  } catch (error) {
    console.error('❌ Error creando préstamo:', error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Actualizar un préstamo
 */
export const actualizarPrestamo = async (id, prestamoData) => {
  try {
    if (!id) throw new Error('ID de préstamo es requerido');
    console.log(`✏️ Actualizando préstamo ID: ${id}`, prestamoData);
    const response = await apiFetch(`/Prestamo/${id}`, {
      method: 'PUT',
      body: JSON.stringify(prestamoData)
    });
    return {
      success: true,
      data: convertirDesdeAPI(response),
      message: 'Préstamo actualizado exitosamente'
    };
  } catch (error) {
    console.error(`❌ Error actualizando préstamo ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Cancelar un préstamo
 */
export const cancelarPrestamo = async (id) => {
  try {
    if (!id) throw new Error('ID de préstamo es requerido');
    console.log(`🗑️ Cancelando préstamo ID: ${id}`);
    const response = await apiFetch(`/Prestamo/${id}`, {
      method: 'DELETE'
    });
    return {
      success: true,
      data: response,
      message: response.message || 'Préstamo cancelado exitosamente'
    };
  } catch (error) {
    console.error(`❌ Error cancelando préstamo ${id}:`, error);
    return { success: false, error: error.message };
  }
};

/**
 * Registrar un abono/pago
 */
export const registrarAbono = async (abonoData) => {
  try {
    if (!abonoData.idPrestamo) throw new Error('ID de préstamo es requerido');
    if (abonoData.montoUsd <= 0 && abonoData.montoBs <= 0) {
      throw new Error('Debe especificar al menos un monto en USD o Bs');
    }
    if (abonoData.tasaBsUsd <= 0) throw new Error('La tasa de cambio debe ser mayor a 0');
    if (!abonoData.idUsuarioRegistro) throw new Error('ID de usuario registro es requerido');

    console.log('💰 Registrando abono:', abonoData);
    const response = await apiFetch('/Prestamo/abono', {
      method: 'POST',
      body: JSON.stringify(abonoData)
    });

    return {
      success: true,
      data: response,
      message: 'Abono registrado exitosamente'
    };
  } catch (error) {
    console.error('❌ Error registrando abono:', error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Registrar pago rápido
 */
export const registrarPagoRapido = async (pagoData) => {
  try {
    if (!pagoData.idPrestamo) throw new Error('ID de préstamo es requerido');
    if (pagoData.montoUsd <= 0 && pagoData.montoBs <= 0) {
      throw new Error('Debe especificar al menos un monto en USD o Bs');
    }
    if (pagoData.tasaBsUsd <= 0) throw new Error('La tasa de cambio debe ser mayor a 0');
    if (!pagoData.idUsuarioRegistro) throw new Error('ID de usuario registro es requerido');

    console.log('💰 Registrando pago rápido:', pagoData);
    const response = await apiFetch('/Prestamo/pago-rapido', {
      method: 'POST',
      body: JSON.stringify(pagoData)
    });

    return {
      success: true,
      data: response,
      message: 'Pago rápido registrado exitosamente'
    };
  } catch (error) {
    console.error('❌ Error registrando pago rápido:', error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Obtener estadísticas de préstamos
 */
export const obtenerEstadisticasPrestamos = async () => {
  try {
    console.log('📊 Obteniendo estadísticas de préstamos...');
    const response = await apiFetch('/Prestamo/estadisticas');
    return {
      success: true,
      data: response,
      message: 'Estadísticas obtenidas'
    };
  } catch (error) {
    console.error('❌ Error obteniendo estadísticas:', error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Obtener resumen por prestatario
 */
export const obtenerResumenPrestatarios = async () => {
  try {
    console.log('📊 Obteniendo resumen por prestatario...');
    const response = await apiFetch('/Prestamo/resumen-prestatarios');
    return {
      success: true,
      data: response,
      message: 'Resumen obtenido'
    };
  } catch (error) {
    console.error('❌ Error obteniendo resumen:', error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Obtener resumen de un prestatario específico
 */
export const obtenerResumenPrestatario = async (filtros) => {
  try {
    console.log('📊 Obteniendo resumen de prestatario:', filtros);
    const params = new URLSearchParams();
    if (filtros.idUsuario) params.append('idUsuario', filtros.idUsuario);
    if (filtros.idCliente) params.append('idCliente', filtros.idCliente);
    if (filtros.tipo) params.append('tipo', filtros.tipo);
    
    const response = await apiFetch(`/Prestamo/resumen-prestatario?${params.toString()}`);
    return {
      success: true,
      data: response,
      message: 'Resumen obtenido'
    };
  } catch (error) {
    console.error('❌ Error obteniendo resumen de prestatario:', error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Calcular cuotas de un préstamo
 */
export const calcularCuotas = async (idPrestamo) => {
  try {
    if (!idPrestamo) throw new Error('ID de préstamo es requerido');
    console.log(`📊 Calculando cuotas del préstamo ${idPrestamo}...`);
    const response = await apiFetch(`/Prestamo/calcular-cuotas/${idPrestamo}`);
    return {
      success: true,
      data: response,
      message: 'Cuotas calculadas'
    };
  } catch (error) {
    console.error(`❌ Error calculando cuotas ${idPrestamo}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

// ==================== EXPORT DEFAULT ====================

export default {
  ESTADOS_PRESTAMO,
  TIPOS_PRESTATARIO,
  METODOS_PAGO_PRESTAMO,
  
  obtenerPrestamos,
  obtenerPrestamosActivos,
  obtenerPrestamosVencidos,
  obtenerPrestamosProximosVencer,
  obtenerPrestamosPorEstado,
  obtenerPrestamosPorTipo,
  obtenerPrestamosPorUsuario,
  obtenerPrestamosPorCliente,
  obtenerPrestamoPorId,
  obtenerPrestamoDetalle,
  obtenerPrestamoPorNumero,
  buscarPrestamos,
  crearPrestamo,
  actualizarPrestamo,
  cancelarPrestamo,
  registrarAbono,
  registrarPagoRapido,
  obtenerEstadisticasPrestamos,
  obtenerResumenPrestatarios,
  obtenerResumenPrestatario,
  calcularCuotas,
  
  formatearEstadoPrestamo,
  formatearTipoPrestatario,
  formatearMetodoPago,
  calcularDiasParaVencer,
  obtenerEstadoVencimiento,
  
  convertirDesdeAPI,
  convertirParaAPI,
  convertirCuotaDesdeAPI,
  convertirAbonoDesdeAPI
};